/**
 * hooks/useVendorAccess.js
 *
 * Client-side gate for the VENDOR TEAM permissions (docs/commerce/
 * vendor-team-roles.md). Companion to the server resolver behind
 *   GET /api/vendor-team/:vendorUserId/me
 *      -> { vendorUserId, userId, roleKey, source, isOwner, isSuperUser, permissions: [] }
 *
 * Who is gated: a SELLER login (the owner or one of their helpers). The
 * vendor scope is `result.parentVendorId || result._id`, the owner's Users id.
 * Platform staff (LUSUKU STAFF) and every non-seller account are never gated
 * here: `can` answers true and `loaded` is true without a request.
 *
 * Fail-OPEN while loading, exactly like utilities/usePermissions.js: until a
 * definitive permission set arrives `can()` returns true so no control flashes
 * in and out on first paint. A 403 from the endpoint IS a definitive answer
 * (a suspended member, or no membership at all) and resolves to no
 * permissions; any other failure keeps failing open because the server is the
 * real enforcement boundary.
 *
 * Branches (4 Oct 2026). /me also answers `branchScope`, `isBranchScoped` and
 * `locations` (the locations this login may act on). A branch member keeps
 * the read keys, never holds the head office keys, and holds the product
 * level keys (catalogue:activate, pricing:edit) only at a branch, so:
 *
 *   can(key)            the screen-level answer. False for a branch member on
 *                       the head office keys and the product level keys; the
 *                       data of every other screen is already their branches.
 *   canAt(location, key) the answer at one location ("main" or an outlet id).
 *                       Without `key` it says whether the location is in scope;
 *                       "all" or no location is head office only.
 *
 * Both fail open while loading, like can() always has; the branch fields are
 * empty until /me answers, so nothing locks before the scope is known.
 *
 * Shape: { loading, loaded, vendorUserId, roleKey, source, isOwner,
 *          permissions, branchScope, isBranchScoped, locations,
 *          can(key), canAny(keys), canAt(location, key), refresh }
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiVendorTeamMyAccess, unwrapVendorTeam } from "../api/vendorTeam.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import {
  HQ_ONLY_KEYS,
  PRODUCT_LEVEL_KEYS,
  normalizeBranchScope,
  scopeSetOf,
  scopedAllows,
  vendorOwnerIdOf,
} from "../utilities/vendorScope.js";

const VendorAccessContext = createContext(null);

const WILDCARD = "*";

const toKeyArray = (perms) =>
  Array.isArray(perms) ? perms.filter((p) => typeof p === "string" && p.length > 0) : [];

/** The vendor scope for a login, or null when this login is not gated. */
export const vendorScopeFor = (user) => {
  const me = user?.result;
  if (!me || me.userType !== "SELLER") return null;
  return vendorOwnerIdOf(me);
};

const WHOLE_BUSINESS = normalizeBranchScope(null);

const toLocations = (list) =>
  Array.isArray(list)
    ? list
        .filter((l) => l && l.id)
        .map((l) => ({ id: String(l.id), name: l.name || null, isActive: l.isActive !== false, isMain: Boolean(l.isMain) }))
    : [];

const UNGATED = {
  loading: false,
  loaded: true,
  vendorUserId: null,
  roleKey: null,
  source: "NONE",
  isOwner: false,
  permissions: [WILDCARD],
  branchScope: WHOLE_BUSINESS,
  locations: [],
};

export const VendorAccessProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    const scope = vendorScopeFor(getUserFromLocalStorage());
    if (!scope) return UNGATED;
    return {
      loading: true,
      loaded: false,
      vendorUserId: scope,
      roleKey: null,
      source: null,
      isOwner: false,
      permissions: null,
      branchScope: WHOLE_BUSINESS,
      locations: [],
    };
  });

  const load = useCallback(async () => {
    const scope = vendorScopeFor(getUserFromLocalStorage());
    if (!scope) {
      setState(UNGATED);
      return;
    }
    setState((s) => ({ ...s, loading: true, vendorUserId: scope }));
    try {
      const res = await apiVendorTeamMyAccess(scope);
      const data = unwrapVendorTeam(res);
      const perms = toKeyArray(data.permissions);
      setState({
        loading: false,
        loaded: true,
        vendorUserId: String(data.vendorUserId || scope),
        roleKey: data.roleKey || null,
        source: data.source || null,
        isOwner: Boolean(data.isOwner) || Boolean(data.isSuperUser),
        permissions: data.isOwner || data.isSuperUser ? [WILDCARD, ...perms] : perms,
        // The owner and an impersonating super user always run the whole
        // business, whatever a stale row says.
        branchScope: data.isOwner || data.isSuperUser ? WHOLE_BUSINESS : normalizeBranchScope(data.branchScope),
        locations: toLocations(data.locations),
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        // Definitive: this login has no standing on the vendor (suspended,
        // removed, or never a member). Fail closed.
        const body = err?.response?.data || {};
        setState({
          loading: false,
          loaded: true,
          vendorUserId: scope,
          roleKey: body.roleKey || null,
          source: body.source || "NONE",
          isOwner: false,
          permissions: [],
          branchScope: WHOLE_BUSINESS,
          locations: [],
        });
        return;
      }
      // eslint-disable-next-line no-console
      console.warn("[useVendorAccess] fetch failed:", err?.message);
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    load();
    const onStorage = (e) => {
      if (e.key === "token" || e.key === "profile") load();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [load]);

  const value = useMemo(() => {
    const { loaded, isOwner, permissions } = state;
    const keys = toKeyArray(permissions);
    const scopeSet = isOwner || !loaded ? null : scopeSetOf(state.branchScope);
    const isBranchScoped = scopeSet !== null;
    const has = (key) => {
      if (!key || typeof key !== "string") return true;
      return keys.includes(WILDCARD) || keys.includes(key);
    };
    // A branch member never holds a head office key, and a product level key
    // only at a branch (canAt), so the screen-level answer is no.
    const branchLocks = (key) =>
      isBranchScoped && (HQ_ONLY_KEYS.includes(key) || PRODUCT_LEVEL_KEYS.includes(key));
    const holds = (key) => has(key) && !branchLocks(key);
    const can = (key) => {
      if (isOwner) return true;
      if (!loaded) return true;
      return holds(key);
    };
    const canAny = (list) => {
      if (isOwner || !loaded) return true;
      if (!Array.isArray(list) || list.length === 0) return true;
      return list.some(holds);
    };
    const canAt = (location, key) => {
      if (isOwner || !loaded) return true;
      return scopedAllows({ has, scopeSet }, key, location);
    };
    return { ...state, isBranchScoped, can, canAny, canAt, refresh: load };
  }, [state, load]);

  return (
    <VendorAccessContext.Provider value={value}>
      {children}
    </VendorAccessContext.Provider>
  );
};

/**
 * Reads VendorAccessContext. Outside the provider it returns an ungated,
 * fail-open shape so a stray mount can never blank a vendor's screen.
 */
export const useVendorAccess = () => {
  const ctx = useContext(VendorAccessContext);
  if (!ctx) {
    return {
      ...UNGATED,
      loaded: false,
      isBranchScoped: false,
      can: () => true,
      canAny: () => true,
      canAt: () => true,
      refresh: () => {},
    };
  }
  return ctx;
};

/** The line a branch member sees above a list cut down to their branches. */
export const MY_BRANCHES_ONLY = "Showing only the branches you work at.";
/** ...and when none of the listed outlets is one of theirs. */
export const NOT_YOUR_OUTLETS = "None of these outlets are yours to run. Ask the owner of the business if this looks wrong.";

/**
 * The outlets of `outlets` this login runs (4 Oct 2026). Head office, every
 * login without a branch limit and platform staff get the list as it is; a
 * member limited to some branches gets their own branches only, so an outlet
 * screen does not show them a row of locked branches that are not theirs.
 * Rendering only: anything that writes the whole list back keeps the input.
 *
 * @returns {{ outlets: Array, limited: boolean }}
 */
export const useOutletsInScope = (outlets) => {
  const { isBranchScoped, canAt } = useVendorAccess();
  return useMemo(() => {
    const list = Array.isArray(outlets) ? outlets : [];
    if (!isBranchScoped) return { outlets: list, limited: false };
    return { outlets: list.filter((o) => canAt(o?._id || o?.id)), limited: true };
  }, [outlets, isBranchScoped, canAt]);
};

export default useVendorAccess;
